import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building, Users, Calendar, Eye, Edit3, Trash2 } from 'lucide-react';

// Table Header Component
export const CompanyListHeader = () => {
  return (
    <thead className="table-light">
      <tr>
        <th scope="col" className="border-0 fw-bold">Company</th>
        <th scope="col" className="border-0 fw-bold">Location</th>
        <th scope="col" className="border-0 fw-bold">Size</th>
        <th scope="col" className="border-0 fw-bold">Status</th>
        <th scope="col" className="border-0 fw-bold text-center">Job Posts</th>
        <th scope="col" className="border-0 fw-bold text-center">Applications</th>
        <th scope="col" className="border-0 fw-bold text-center">Hired</th>
        <th scope="col" className="border-0 fw-bold text-center">Actions</th>
      </tr>
    </thead>
  );
};

// Table Row Component
const CompanyListItem = ({ company, onView, onEdit, onDelete }) => {
  const AWS = process.env.REACT_APP_AWS_HOST;
  const navigate = useNavigate();

  // Handle total jobs click - redirect to jobs page with company filter
  const handleTotalJobsClick = () => {
    // Navigate to jobs page with company name as search parameter
    navigate(`/job-page?company=${encodeURIComponent(company.name)}`);
  };

  // Handle total hired click - redirect to student application list with filters
  const handleTotalHiredClick = () => {
    // Navigate to student application list with organization ID and hired status
    navigate(`/student-application?org_id=${company.id}&status=hired`);
  };

  // Handle total applications click - redirect to student application list with company filter
  const handleTotalApplicationsClick = () => {
    // Navigate to student application list with organization ID
    navigate(`/student-application?org_id=${company.id}`);
  };
  
  return (
    <tr className="hover-shadow">
      {/* Company Info */}
      <td className="align-middle py-3">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 me-3">
            <img
              src={company.logo ? `${AWS}${company.logo}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=random`}
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=random`;
              }}
              alt='logo'
              className="border"
              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
            />
          </div>
          <div className="flex-grow-1">
            <h6 className="mb-0 text-dark fw-bold">{company.name}</h6>
            <small className="text-primary text-truncate d-block" style={{ maxWidth: '200px' }}>
              {company.about}
            </small>
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>
              {company.industryName}
            </small>
          </div>
        </div>
      </td>

      {/* Location */}
      <td className="align-middle">
        <div className="d-flex align-items-center text-primary">
          <MapPin size={14} className="me-1" />
          <span>{company.city}, {company.state}</span>
        </div>
      </td>

      {/* Size */}
      <td className="align-middle">
        <div className="d-flex align-items-center text-primary">
          <Users size={14} className="me-1" />
          <span>{company.size}</span>
        </div>
      </td>

      {/* Status */}
      <td className="align-middle">
        <span className={`badge ${company.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
          {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
        </span>
      </td>

      {/* Job Posts - Now Clickable */}
      <td className="align-middle text-center">
        <div 
          className="fw-bold text-primary"
          onClick={handleTotalJobsClick}
          style={{ cursor: 'pointer' }}
          title="View all jobs from this company"
        >
          {company.totalPosts}
        </div>
      </td>

      {/* Applications - New Clickable Column */}
      <td className="align-middle text-center">
        <div 
          className="fw-bold text-info"
          onClick={handleTotalApplicationsClick}
          style={{ cursor: 'pointer' }}
          title="View all applications from this company"
        >
          {company.totalApplications || 0}
        </div>
      </td>

      {/* Hired - Now Clickable */}
      <td className="align-middle text-center">
        <div 
          className="fw-bold text-success"
          onClick={handleTotalHiredClick}
          style={{ cursor: 'pointer' }}
          title="View hired candidates from this company"
        >
          {company.totalHired}
        </div>
      </td>

      {/* Actions */}
      <td className="align-middle text-center">
        <div className="d-flex gap-1 justify-content-center">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => onView(company)}
            title="View Details"
          >
            <Eye size={14} />
          </button>
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() => onEdit(company)}
            title="Edit Company"
          >
            <Edit3 size={14} />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => onDelete(company)}
            title="Delete Company"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Complete Table Component (Optional - for reference)
export const CompanyTable = ({ companies, onView, onEdit, onDelete }) => {
  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <CompanyListHeader />
        <tbody>
          {companies.map((company) => (
            <CompanyListItem
              key={company.id}
              company={company}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyListItem;